import React from 'react';
import { useTheme } from '../theme/ThemeContext';
import { getUnlockCost } from '../game/mechanics/penalty';

interface UnlockDialogProps {
  boxX: number;
  boxY: number;
  failureCount: number;
  rewardPoints: number;
  onConfirmUnlock: (boxX: number, boxY: number) => void;
  onClose: () => void;
}

export const UnlockDialog: React.FC<UnlockDialogProps> = ({
  boxX,
  boxY,
  failureCount,
  rewardPoints,
  onConfirmUnlock,
  onClose,
}) => {
  const { theme } = useTheme();
  const cost = getUnlockCost(failureCount);
  const canAfford = rewardPoints >= cost;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '24px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '360px',
          backgroundColor: theme.isDark ? '#18181b' : '#ffffff',
          borderRadius: '24px',
          padding: '28px',
          border: `1px solid ${theme.isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)'}`,
          boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
          color: theme.textColor,
          fontFamily: '"Outfit", sans-serif',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '48px', marginBottom: '12px' }}>💥</div>

        <h2 style={{ margin: '0 0 8px 0', fontSize: '22px', fontWeight: 800 }}>
          Box ({boxX}, {boxY}) Busted
        </h2>

        <p style={{ margin: '0 0 20px 0', opacity: 0.8, fontSize: '14px' }}>
          Unlock this 10×10 box to try again. The mine layout will remain unchanged.
        </p>

        <div
          style={{
            backgroundColor: theme.isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
            borderRadius: '16px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px' }}>
            <span style={{ opacity: 0.7 }}>Unlock Cost:</span>
            <span style={{ fontWeight: 700, color: '#eab308' }}>⭐ {cost}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px' }}>
            <span style={{ opacity: 0.7 }}>Your Balance:</span>
            <span style={{ fontWeight: 700 }}>⭐ {rewardPoints}</span>
          </div>
        </div>

        {!canAfford && (
          <p
            style={{
              color: '#ef4444',
              fontSize: '13px',
              fontWeight: 600,
              margin: '0 0 16px 0',
            }}
          >
            You need {cost - rewardPoints} more reward points to unlock this box. Complete other
            boxes to earn points!
          </p>
        )}

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: '12px',
              backgroundColor: 'transparent',
              border: `1px solid ${
                theme.isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
              }`,
              color: theme.textColor,
              fontFamily: '"Outfit", sans-serif',
              fontWeight: 600,
              fontSize: '15px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>

          <button
            disabled={!canAfford}
            onClick={() => onConfirmUnlock(boxX, boxY)}
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: '12px',
              backgroundColor: canAfford ? '#22c55e' : theme.isDark ? '#27272a' : '#e4e4e7',
              border: 'none',
              color: canAfford ? '#ffffff' : theme.isDark ? '#71717a' : '#a1a1aa',
              fontFamily: '"Outfit", sans-serif',
              fontWeight: 700,
              fontSize: '15px',
              cursor: canAfford ? 'pointer' : 'not-allowed',
            }}
          >
            Unlock Box
          </button>
        </div>
      </div>
    </div>
  );
};
